import { useCallback, useEffect, useRef, useState } from 'react';
import type { BackendOccasionType, WizardData } from '@/src/types';
import { createDraft, saveDraft, type DraftPayload } from '@/src/infrastructure/lib/api';

export type DraftSaveStatus = 'idle' | 'saving' | 'saved' | 'error';

const DEBOUNCE_MS = 1000;

/**
 * Build the autosave payload from wizard state.
 *
 * - Structured fields (eventName, venue, host, …) are sent only when they hold
 *   a value, so an empty draft never trips the backend's `@IsNotEmpty` rules.
 * - `templateId` IS always included so the backend can update it when the user
 *   changes templates after the draft has already been created. Without this,
 *   the draft keeps the default templateId (Cinematic Wedding) even when the
 *   user switches to e.g. Lagna Patrika, causing the wrong template to render
 *   after publishing.
 * - `eventDate` is derived from the raw date/time inputs when present.
 * - `draftMeta` carries the *entire* wizard state plus the current step, so the
 *   wizard can rehydrate losslessly — including fields the structured columns
 *   don't hold (`_dateInput`, `_timeInput`, panel state via the caller).
 */
export function buildDraftPayload(data: WizardData, step: number): DraftPayload {
  const eventDate = data._dateInput
    ? new Date(`${data._dateInput}T${data._timeInput || '00:00'}`).toISOString()
    : undefined;

  return {
    // Always include templateId so changing templates after draft creation
    // actually updates the draft's templateId (requires backend support in
    // UpdateInvitationDto + invitation.service.ts updateInvitation).
    ...(data.templateId && { templateId: data.templateId }),
    ...(data.eventName?.trim() && { eventName: data.eventName }),
    ...(data.hostName?.trim() && { hostName: data.hostName }),
    ...(data.eventVenue?.trim() && { eventVenue: data.eventVenue }),
    ...(data.customMessage?.trim() && { customMessage: data.customMessage }),
    ...(data.rsvpLink?.trim() && { rsvpLink: data.rsvpLink }),
    ...(data.mediaUrls?.length ? { mediaUrls: data.mediaUrls } : {}),
    ...(data.customData ? { customData: data.customData } : {}),
    ...(eventDate ? { eventDate } : {}),
    draftMeta: { ...data, step },
  };
}

/** A draft can be saved if an existing draft ID is present, or for new drafts once step >= 3 with template + occasion. */
function canCreate(data: WizardData, step?: number, hasExistingDraft?: boolean): boolean {
  if (hasExistingDraft) return true;
  if (!data.templateId || !data.occasionType) return false;
  // For new drafts, do not create a draft on Step 1 (Occasion) or Step 2 (Template choice alone).
  // Only create the draft once the user enters Step 3 (Details) to begin filling in event details.
  return typeof step === 'number' ? step >= 3 : true;
}

interface Options {
  data: WizardData;
  step: number;
  /** Existing draft id when resuming (from `?draftId=`); null for a new draft. */
  initialDraftId?: string | null;
  /** Pause autosave while the wizard is still hydrating from the server. */
  enabled?: boolean;
}

/**
 * Debounced server-side autosave for the invitation wizard. Creates the DRAFT
 * when the user enters Step 3 and PUTs partial updates thereafter, so work
 * survives refresh, logout, and device switches.
 */
export function useDraftAutosave({ data, step, initialDraftId, enabled = true }: Options) {
  const [draftId, setDraftId] = useState<string | null>(initialDraftId ?? null);
  const [status, setStatus] = useState<DraftSaveStatus>('idle');

  // Refs so the debounced/unmount flush always sees the latest values without
  // re-arming the timer on every keystroke.
  const draftIdRef = useRef<string | null>(initialDraftId ?? null);
  const latest = useRef({ data, step });
  // Holds the in-flight flush's own promise (not just a boolean) so a
  // concurrent caller — e.g. a photo upload's `ensureInvitationId` firing
  // while the debounced autosave is mid-request — awaits the SAME request
  // and gets the real id, instead of reading `draftIdRef.current` before it
  // has been set and failing with "Save your draft first" even though a
  // save was already in progress.
  const inFlightPromise = useRef<Promise<string | null> | null>(null);
  const lastSavedSig = useRef<string>('');
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  latest.current = { data, step };
  useEffect(() => {
    draftIdRef.current = draftId;
  }, [draftId]);

  // When initial hydration finishes for an existing draft, synchronize lastSavedSig
  useEffect(() => {
    if (enabled && initialDraftId && canCreate(data, step, Boolean(initialDraftId))) {
      lastSavedSig.current = JSON.stringify(buildDraftPayload(data, step));
    }
  }, [enabled, initialDraftId, data, step]);

  const flush = useCallback((): Promise<string | null> => {
    // A save is already in flight — return ITS promise rather than racing
    // ahead with whatever draftIdRef currently holds (possibly still null).
    if (inFlightPromise.current) return inFlightPromise.current;

    const { data: d, step: s } = latest.current;
    if (!canCreate(d, s, Boolean(draftIdRef.current))) return Promise.resolve(draftIdRef.current);

    const payload = buildDraftPayload(d, s);
    const sig = JSON.stringify(payload);
    if (sig === lastSavedSig.current) return Promise.resolve(draftIdRef.current);

    const promise = (async () => {
      setStatus('saving');
      try {
        let id = draftIdRef.current;
        if (id) {
          await saveDraft(id, payload);
        } else {
          if (!d.templateId || !d.occasionType) {
            throw new Error('Cannot create draft without templateId and occasionType');
          }
          const created = await createDraft({
            ...payload,
            templateId: d.templateId,
            occasionType: d.occasionType,
          });
          id = created.id;
          draftIdRef.current = id;
          setDraftId(id);
        }
        lastSavedSig.current = sig;
        setStatus('saved');
        return id;
      } catch {
        setStatus('error');
        return draftIdRef.current;
      } finally {
        inFlightPromise.current = null;
      }
    })();

    inFlightPromise.current = promise;
    return promise;
  }, []);

  // Debounce: re-arm a single timer whenever the serialized payload changes.
  useEffect(() => {
    if (!enabled || !canCreate(data, step, Boolean(draftIdRef.current))) return;
    const sig = JSON.stringify(buildDraftPayload(data, step));
    if (sig === lastSavedSig.current) return;

    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      void flush();
    }, DEBOUNCE_MS);

    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
    // `data`/`step` drive the debounce; `flush` is stable.
  }, [data, step, enabled, flush]);

  // Best-effort flush of unsaved work when the wizard unmounts (navigation away).
  useEffect(() => {
    return () => {
      void flush();
    };
  }, [flush]);

  return { draftId, status, saveNow: flush };
}
